import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Form,
  DialogDescription,
} from '@predoc/cow';
import { Pencil, AlertCircle } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import {
  Profile,
  createProfileSchema,
  EditProfileFormData,
  UpdateProfilePayload,
} from '../types';
import { ProfileFormFields } from './ProfileFormFields';
import { Permission, useHasPermission } from '@/utils/roles';
import { UserRole } from '@/feature/users/constants';
import { useUpdateProfile } from '../hooks/profilesAPI';
import { useGetOrganizationInfo } from '../hooks/organizationAPI';
import { useUpdateForm } from '@/hooks';
import { toast } from 'sonner';

export const EditProfileModal = ({ profile }: { profile: Profile }) => {
  const [open, setOpen] = useState(false);
  const { hasPermission, currentUser } = useHasPermission(
    Permission.EDIT_PROFILES,
  );

  const canEdit =
    hasPermission &&
    (currentUser?.role !== UserRole.MANAGER ||
      currentUser.profileAccess.some((p) => p.id === profile.id));
  const { data: organizationInfo } = useGetOrganizationInfo();
  const updateProfile = useUpdateProfile();
  const profileFormData = useMemo(() => {
    return {
      ...profile,
      description: profile.description ?? '',
      defaultFacesheetConfigIds: profile.facesheets
        .filter((facesheet) => facesheet.isDefault)
        .map((facesheet) => facesheet.facesheetConfigId),
      expeditedFacesheetConfigIds: profile.facesheets
        .filter((facesheet) => facesheet.isExpedited)
        .map((facesheet) => facesheet.facesheetConfigId),
      hasExpeditedFacesheets: profile.facesheets.some(
        (facesheet) => facesheet.isExpedited,
      ),
    };
  }, [profile]);
  // Create schema with profile facesheets and organization info for validation
  const profileSchema = useMemo(
    () =>
      createProfileSchema(
        profile.facesheets,
        organizationInfo,
        profile.availableRetrievalMethods,
        profile.facesheets,
      ),
    [profile.facesheets, organizationInfo, profile.availableRetrievalMethods],
  );

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    form.reset(profileFormData);
    setOpen(true);
  };

  const form = useUpdateForm<EditProfileFormData>({
    resolver: zodResolver(profileSchema),
    mode: 'onSubmit',
    defaultValues: profileFormData,
    reValidateMode: 'onSubmit',
  });

  // Submit handler that only receives dirty (changed) fields
  const onSubmit = async (dirtyFields: Partial<EditProfileFormData>) => {
    try {
      const updatePayload: UpdateProfilePayload = {
        organizationProfileId: profile.id,
        ...dirtyFields,
      };
      await updateProfile.mutateAsync(updatePayload);
      toast.success('Profile updated successfully');
      handleClose();
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    }
  };

  if (!canEdit) {
    return null;
  }

  return (
    <>
      <Button variant="outline" onClick={handleOpen} aria-label="Edit profile">
        <Pencil className="size-4" aria-hidden="true" />
        Edit
      </Button>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="mx-auto !max-w-[946px]">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="text-xl font-semibold">
              Edit Profile
            </DialogTitle>
            <DialogDescription className="sr-only">
              Edit profile details and settings
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleUpdateSubmit(onSubmit)}>
              <ProfileFormFields
                form={form}
                profileId={profile.id}
                profileAvailableMethods={profile.availableRetrievalMethods}
                profileFacesheets={profile.facesheets}
              />
              <DialogFooter className="mt-6 border-t pt-4">
                <div className="flex w-full items-center justify-end gap-4">
                  {form.formState.isSubmitted && !form.formState.isValid ? (
                    <div className="text-destructive flex items-center gap-2 text-sm">
                      <AlertCircle className="size-4" />
                      Please fix validation errors.
                    </div>
                  ) : null}
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleClose}
                      aria-label="Cancel"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      aria-label="Save"
                      disabled={!form.formState.isDirty}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
};
